import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useCreateWebsiteSource } from "@/features/documents/hooks/use-website-sources";
import { useTranslation } from "@/features/utils/i18n";
import { GlobeIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

export const DEFAULT_MAX_PAGES = 50;
export const MAX_PAGES_LIMIT = 100;

export const AddWebsiteDialog = () => {
  const { t } = useTranslation("documents");
  const companyWebsite = useCurrentClient()?.client?.website || "";
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [pathPrefix, setPathPrefix] = useState("");
  const [maxPages, setMaxPages] = useState(String(DEFAULT_MAX_PAGES));

  const createSource = useCreateWebsiteSource();

  const onOpenChange = (next: boolean) => {
    if (next) {
      // The company's own site is what almost everybody adds first.
      setUrl(companyWebsite);
      setPathPrefix("");
      setMaxPages(String(DEFAULT_MAX_PAGES));
    }
    setOpen(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    try {
      await createSource.mutateAsync({
        url: url.trim(),
        path_prefix: pathPrefix.trim(),
        max_pages: Number(maxPages) || DEFAULT_MAX_PAGES,
      });
      toast.success(t("website.messages.addSuccess"));
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("website.messages.addError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <GlobeIcon className="h-4 w-4" weight="fill" />
          {t("website.add")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit} className="min-w-0 space-y-4">
          <DialogHeader>
            <DialogTitle>{t("website.dialog.title")}</DialogTitle>
            <DialogDescription>
              {t("website.dialog.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="website-url">{t("website.dialog.url")}</Label>
            <Input
              id="website-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.exemple.fr"
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <div className="space-y-2">
              <Label htmlFor="website-max-pages">
                {t("website.dialog.maxPages")}
              </Label>
              <Input
                id="website-max-pages"
                type="number"
                min={1}
                max={MAX_PAGES_LIMIT}
                value={maxPages}
                onChange={(e) => setMaxPages(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website-prefix">
              {t("website.dialog.pathPrefix")}
            </Label>
            <Input
              id="website-prefix"
              value={pathPrefix}
              onChange={(e) => setPathPrefix(e.target.value)}
              placeholder="/blog"
            />
            <p className="text-xs text-muted-foreground">
              {t("website.dialog.scopeHint", { max: MAX_PAGES_LIMIT })}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("upload.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createSource.isPending || !url.trim()}
            >
              {createSource.isPending && (
                <Spinner className="h-4 w-4 animate-spin" />
              )}
              {t("website.dialog.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
